// ============================================================
// TMR Portal Admin — Cadastro de empresa: passos, validação e campos
// Compartilhado pelo wizard "Nova empresa" e pela edição do detalhe.
// ============================================================
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Segmented } from '../../components/primitives';
import { TextField, SelectField } from '../../components/fields';
import { PhoneField } from '../../components/PhoneField';
import { ComboField } from '../../components/ComboField';
import { Notice } from '../../components/Notice';
import { Hint } from '../../components/Tooltip';
import { Field } from '../../components/ui';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCNPJ, maskCEP, maskCPF, maskSubdomain, maskDigits, onlyDigits, isValidCNPJ, isValidCPF, isEmail } from '../../lib/format';
import { displayPhone, isValidInternationalPhone } from '../../lib/phone';
import { ORIGENS, ESTADOS } from '../../data/reference';
import { lookupCep } from '../../data/companies/cep';
import { classificationLabel, useClassifications } from '../../data/companies/queries';
import { COMPANY_SEGMENTS, segmentLabel } from '../../data/companies/segment';
import { SUBDOMAIN_MAX_LENGTH } from '../../data/companies/subdomain';
import {
  DEFAULT_LEGACY_PORTAL_PERMISSIONS,
  LEGACY_PORTAL_PERMISSIONS,
} from '../../data/companies/portalPermissions';
import { contactEmailSummary, formContactEmail } from '../../data/companies/contactEmail';
import type { ContactEmailSource } from '../../data/companies/contract';
import { editPerson } from '../../data/companies/form';
import type { CompanyForm } from '../../data/companies/form';
import { validateBankAccount } from '../../data/finance/form';
import { useBanks } from '../../data/finance/queries';
import type { PartyOption } from '../../data/parties/contract';
import type { PartyFormValues } from '../../data/parties/form';
import { CompanyManagerField } from './CompanyManagerField';
import { PartyNameField } from './PartyNameField';

/** Sets one field of the registration form. */
export type SetField = <K extends keyof CompanyForm>(key: K, value: CompanyForm[K]) => void;

/** Validation messages, keyed by the field they belong to. */
export type CompanyFormErrors = Partial<Record<keyof CompanyForm, string>>;

/** Props every step receives. */
export interface StepProps {
  form: CompanyForm;
  set: SetField;
  errors: CompanyFormErrors;
  /** Rendered under the subdomain field — the wizard offers a free suggestion, the edit screen does not. */
  subdomainHint?: ReactNode;
  /** True on the edit screen: the CNPJ is shown but cannot be changed. */
  cnpjLocked?: boolean;
}

// ---- validação por passo ----
export function validateCompanyStep(f: CompanyForm, options: { requireCnpj: boolean }): CompanyFormErrors {
  const e: CompanyFormErrors = {};
  if (!f.name.trim()) e.name = 'Informe o nome fantasia';
  if (!f.legalName.trim()) e.legalName = 'Informe a razão social';
  if (options.requireCnpj && !isValidCNPJ(f.cnpj)) e.cnpj = 'CNPJ inválido';
  // The backend caps the subdomain at 16 characters — the width of the legacy column it names.
  if (!new RegExp(`^[a-z0-9][a-z0-9-]{1,${SUBDOMAIN_MAX_LENGTH - 2}}[a-z0-9]$`).test(f.subdomain)) {
    e.subdomain = `Use 3–${SUBDOMAIN_MAX_LENGTH} letras minúsculas, números ou hífen`;
  }
  return e;
}

export function validateAddressStep(f: CompanyForm): CompanyFormErrors {
  const e: CompanyFormErrors = {};
  if (!/^\d{5}-\d{3}$/.test(f.zipCode)) e.zipCode = 'CEP inválido';
  if (!f.state) e.state = 'Selecione o estado';
  if (!f.city.trim()) e.city = 'Informe a cidade';
  if (!f.district.trim()) e.district = 'Informe o bairro';
  if (!f.street.trim()) e.street = 'Informe o logradouro';
  if (!f.number.trim()) e.number = 'Informe o número (ou S/N)';
  return e;
}

export function validateContactStep(f: CompanyForm): CompanyFormErrors {
  const e: CompanyFormErrors = {};
  if (!f.representativeName.trim()) e.representativeName = 'Informe o representante';
  if (!isEmail(f.representativeEmail)) e.representativeEmail = 'E-mail inválido';
  if (f.representativeCpf && !isValidCPF(f.representativeCpf)) e.representativeCpf = 'CPF inválido';
  if (!isValidInternationalPhone(f.representativePhone)) e.representativePhone = 'Telefone inválido';
  if (!f.focalContactName.trim()) e.focalContactName = 'Informe o contato focal';
  if (!isValidInternationalPhone(f.focalContactPhone)) e.focalContactPhone = 'Telefone inválido';
  // The choice is never switched behind the operator's back: clearing the focal e-mail while it is the
  // chosen source is flagged on both, so they either fill it in again or pick another source.
  if (f.contactEmailSource === 'FocalContact') {
    if (!f.focalContactEmail.trim()) {
      e.focalContactEmail = 'Informe o e-mail do contato focal';
      e.contactEmailSource = 'O contato focal está sem e-mail. Informe-o ou escolha outra opção.';
    } else if (!isEmail(f.focalContactEmail)) {
      e.focalContactEmail = 'E-mail inválido';
    }
  }
  if (f.contactEmailSource === 'Other' && !isEmail(f.contactEmail)) {
    e.contactEmail = f.contactEmail.trim() ? 'E-mail inválido' : 'Informe o e-mail de contato';
  }
  return e;
}

/**
 * The receiving account the registration collects, checked by the Finance module's own rules.
 *
 * The block stays optional and all-or-nothing — like the backend's value object, which refuses half an
 * account — but **what "valid" means is no longer this screen's opinion**. It delegates to
 * {@link validateBankAccount}, the same function the Conta bancária screen uses, so a branch of two digits
 * is refused here exactly as it is there. Its own copy of the rules was looser and never checked the bank
 * code at all, which the operator only discovered after a round-trip.
 */
export function validateReceivingAccountStep(f: CompanyForm): CompanyFormErrors {
  const e: CompanyFormErrors = {};
  // Touching one field asks for the whole account; touching none leaves it for the financial onboarding.
  const started =
    f.bankCode || f.bankName || f.bankBranch || f.bankAccountNumber || f.bankAccountDigit
      || f.bankHolderLegalName.trim();
  if (!started) return e;

  // The one rule the step keeps for itself: `BankAccountDetailsInput` has no empty account type, so the
  // shared validator has nothing to say about a form where the operator has not chosen one yet.
  if (!f.bankAccountType) e.bankAccountType = 'Selecione o tipo de conta';

  const errors = validateBankAccount({
    holderLegalName: f.bankHolderLegalName,
    bankName: f.bankName,
    bankCode: f.bankCode,
    accountType: f.bankAccountType || 'Checking',
    branch: f.bankBranch,
    number: f.bankAccountNumber,
    digit: f.bankAccountDigit,
  });

  // The account's fields are named after the Empresa form here and after the account there, so the messages
  // are carried across one by one rather than spread — a mismatched key would silently show nothing.
  if (errors.holderLegalName) e.bankHolderLegalName = errors.holderLegalName;
  if (errors.bankName) e.bankName = errors.bankName;
  if (errors.bankCode) e.bankCode = errors.bankCode;
  if (errors.branch) e.bankBranch = errors.branch;
  if (errors.number) e.bankAccountNumber = errors.number;
  if (errors.digit) e.bankAccountDigit = errors.digit;

  return e;
}

/** The rates block: every field optional, each a percentage. */
export function validateRatesStep(f: CompanyForm): CompanyFormErrors {
  const e: CompanyFormErrors = {};
  const percent = (v: string) => v === '' || (!isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100);
  if (!percent(f.monthlyInterest)) e.monthlyInterest = 'Informe um valor entre 0 e 100';
  if (!percent(f.rateStandard)) e.rateStandard = 'Informe um valor entre 0 e 100';
  if (!percent(f.rateShared)) e.rateShared = 'Informe um valor entre 0 e 100';
  if (!percent(f.rateCoProducer)) e.rateCoProducer = 'Informe um valor entre 0 e 100';
  return e;
}

/** One step of the wizard: its label and the rule that lets the operator move on. */
export interface CompanyStep {
  label: string;
  validate: (f: CompanyForm) => CompanyFormErrors;
}

/**
 * The wizard's steps, in order. Onboarding is the only path that asks for a CNPJ.
 *
 * The account and the rates are **two steps, not one**: they were a single "Recebimentos" block, and the
 * two things inside it have nothing to do with each other. One is where the Empresa's money lands — a bank
 * account, saved with the registration; the other is what the TMR charges, which this screen does not save
 * at all. Reading them as one subject is what made the second easy to mistake for the first.
 */
export const COMPANY_STEPS: CompanyStep[] = [
  { label: 'Empresa', validate: (f) => validateCompanyStep(f, { requireCnpj: true }) },
  { label: 'Endereço', validate: validateAddressStep },
  { label: 'Contato & representante', validate: validateContactStep },
  { label: 'Acesso no Portal antigo', validate: () => ({}) },
  { label: 'Conta de recebimento', validate: validateReceivingAccountStep },
  { label: 'Taxas', validate: validateRatesStep },
  { label: 'Revisão', validate: () => ({}) },
];

/** Every rule at once — what the edit screen checks, since it has no steps to walk through. */
export function validateCompanyForm(f: CompanyForm, options: { requireCnpj: boolean }): CompanyFormErrors {
  return {
    ...validateCompanyStep(f, options),
    ...validateAddressStep(f),
    ...validateContactStep(f),
    ...validateReceivingAccountStep(f),
    ...validateRatesStep(f),
  };
}

/**
 * The block's current values as the person form opens with them (10 B5b).
 *
 * It hands over the masked text, not a payload: what the operator sees in the block is what the form shows
 * them, and turning it into `null`s is the form's job at submit time. The focal contact block passes an
 * empty CPF because it has no such field — the form is where that person's CPF can finally be recorded.
 */
function partyDraft(name: string, cpf: string, email: string, phone: string): PartyFormValues {
  return { name, cpf, email, phone, note: '' };
}

// A picked Party fills the block's flat fields — masks re-applied so the fields read as the operator
// typed them — and, since B8.4, its id: that is what the payload carries, and what makes the reference
// exact instead of a re-match on the details. Each block owns which fields it has: the focal contact
// carries no CPF.
function fillFocalContact(set: SetField, p: PartyOption) {
  set('focalContactPartyId', p.id);
  set('focalContactName', p.name);
  set('focalContactPhone', displayPhone(p.phone));
  set('focalContactEmail', p.email ?? '');
}

function fillRepresentative(set: SetField, p: PartyOption) {
  set('representativePartyId', p.id);
  set('representativeName', p.name);
  set('representativeCpf', p.cpf ? maskCPF(p.cpf) : '');
  set('representativeEmail', p.email ?? '');
  set('representativePhone', displayPhone(p.phone));
}

/**
 * The Empresa's size band, from the catalogue the legacy owns (12 §9).
 *
 * It is a component rather than a `SelectField` with an options prop because the options are a request:
 * they used to be four constants declared next to this file, which matched nothing in the V1 and were
 * exported to nowhere.
 *
 * The field is optional, so a catalogue that will not load must not stop a registration: it disables and
 * says so, rather than blocking the form or — worse — offering an empty list that reads as "there are no
 * classifications". The backend serves a fallback catalogue where there is no legacy at all, so an empty
 * answer here really does mean the environment has none active.
 */
function ClassificationField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const query = useClassifications();
  const options = query.data ?? [];

  if (query.isSuccess && options.length === 0) return null;

  return (
    <div>
      <SelectField
        label="Classificação"
        value={value}
        onChange={onChange}
        disabled={!query.isSuccess}
        placeholder={query.isPending ? 'Carregando…' : 'Selecione (opcional)'}
        options={options.map((c) => ({ value: String(c.id), label: classificationLabel(c) }))}
      />
      {query.isError && (
        <p style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 4 }}>
          Não foi possível carregar as classificações agora. O campo é opcional — o restante do cadastro
          segue normalmente.
        </p>
      )}
    </div>
  );
}

// ---- passos ----
export function StepCompanyGeneral({ form, set, errors, subdomainHint, cnpjLocked }: StepProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="pa-grid-2">
        <TextField label="Empresa" required value={form.name} onChange={(v) => set('name', v)} error={errors.name} placeholder="Ex.: Clínica Vida Nova" />
        <TextField label="Razão social" required value={form.legalName} onChange={(v) => set('legalName', v)} error={errors.legalName} placeholder="Ex.: Vida Nova Serviços Médicos Ltda." />
      </div>
      <div className="pa-grid-2">
        {/* Na edição o CNPJ é só leitura: ele identifica a empresa no legado e é a chave do export. */}
        <TextField label="CNPJ" required value={maskCNPJ(form.cnpj)} onChange={cnpjLocked ? undefined : (v) => set('cnpj', maskCNPJ(v))} error={errors.cnpj} placeholder="Ex.: 11.444.777/0001-61" mono disabled={cnpjLocked} />
        <div>
          <label className="pa-form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Hint tip="Este campo irá definir o Diretório do Produto e a URL de Solicitação de Parcelamento.">Domínio</Hint> <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <TextField value={form.subdomain} onChange={(v) => set('subdomain', maskSubdomain(v))} error={errors.subdomain} placeholder="ex.: vidanova" mono maxLength={SUBDOMAIN_MAX_LENGTH} />
          {subdomainHint}
        </div>
      </div>
      <div>
        <label className="pa-form-label">Descrição</label>
        <textarea
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          rows={3}
          placeholder="Notas internas sobre esta empresa (opcional)"
          className="pa-input"
          style={{ width: '100%', padding: '10px 12px', fontFamily: 'var(--font-sans)', fontSize: 14, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)', color: 'var(--ink-900)', outline: 'none', resize: 'vertical', lineHeight: 1.5 }}
        />
      </div>
      <div className="pa-grid-2">
        <SelectField label="Origem da empresa" value={form.acquisitionOrigin} onChange={(v) => set('acquisitionOrigin', v)} placeholder="Selecione (opcional)" options={ORIGENS.map((o) => ({ value: o, label: o }))} />
        <ClassificationField value={form.classification} onChange={(v) => set('classification', v)} />
      </div>
      <div className="pa-grid-2">
        {/* The catalogue is the V2's own and fixed (32 §5), so unlike the classification there is no
            request behind these options and no loading or error state to show. */}
        <SelectField label="Segmento" value={form.segment} onChange={(v) => set('segment', v)} placeholder="Selecione (opcional)" options={COMPANY_SEGMENTS.map((s) => ({ value: s.value, label: s.label }))} />
        <div>
          <label className="pa-form-label">
            <Hint tip="Aplica régua de cobrança diferenciada para esta empresa.">Cobrança especial</Hint>
          </label>
          <Segmented options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} value={form.specialBilling} onChange={(v) => set('specialBilling', v)} size="sm" />
        </div>
      </div>
      {/* Side by side because they are read as a pair — "quem vendeu e quem cuida" — and because seeing
          both at once is what makes it obvious that one is missing. */}
      <div className="pa-grid-2">
        <div>
          <label className="pa-form-label">
            <Hint tip="Quem vendeu esta empresa. É este gestor que o sistema legado recebe.">Gestor de venda</Hint>
          </label>
          <CompanyManagerField form={form} set={set} role="sales" />
        </div>
        <div>
          <label className="pa-form-label">
            <Hint tip="Quem cuida da empresa depois da primeira venda. Existe apenas no TMR.">Gestor de pós-venda</Hint>
          </label>
          <CompanyManagerField form={form} set={set} role="postSales" />
        </div>
      </div>
    </div>
  );
}

/**
 * Where the address block stands relative to the CEP lookup:
 * `idle` before any lookup, `loading` while a provider answers, `ready` once the fields
 * are unlocked — either filled from a hit or released for manual entry after both APIs failed.
 */
type AddressStatus = 'idle' | 'loading' | 'ready';

/** A field-shaped shimmer placeholder shown in place of an input while the CEP is resolving. */
function FieldSkeleton({ label }: { label: string }) {
  return (
    <div aria-busy="true">
      <label className="pa-form-label">{label}</label>
      <div
        style={{
          height: 40,
          borderRadius: 'var(--radius-sm)',
          background: 'linear-gradient(90deg, var(--cream-100) 25%, var(--cream-200) 50%, var(--cream-100) 75%)',
          backgroundSize: '200% 100%',
          animation: 'paShimmer 1.4s var(--ease-out) infinite',
        }}
      />
    </div>
  );
}

export function StepAddress({ form, set, errors }: StepProps) {
  const [zipMessage, setZipMessage] = useState('');
  // An already-filled address (editing an existing company) starts unlocked; a blank one waits for the CEP.
  const [status, setStatus] = useState<AddressStatus>(() =>
    form.street.trim() || form.city.trim() ? 'ready' : 'idle',
  );
  const canLookup = onlyDigits(form.zipCode).length === 8;
  // Manual entry stays blocked until a lookup resolves — filled from a provider or released after both fail.
  const locked = status !== 'ready';
  const loading = status === 'loading';
  const doLookup = async () => {
    if (!canLookup || loading) return;
    setStatus('loading');
    setZipMessage('');
    try {
      const found = await lookupCep(form.zipCode);
      if (found) {
        set('state', found.state);
        set('city', found.city);
        set('district', found.district);
        set('street', found.street);
        setZipMessage('Endereço preenchido automaticamente.');
      } else {
        setZipMessage('CEP não encontrado nas duas bases — preencha manualmente.');
      }
    } finally {
      setStatus('ready');
      setTimeout(() => setZipMessage(''), 3000);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 40px 1fr 120px', gap: 16, alignItems: 'end' }}>
        <TextField label="CEP" required value={maskCEP(form.zipCode)} onChange={(v) => set('zipCode', maskCEP(v))} onBlur={doLookup} error={errors.zipCode} placeholder="00000-000" mono />
        <button type="button" onClick={doLookup} disabled={!canLookup || loading} className="pa-icon-btn" style={{ width: 38, height: 38 }} title="Buscar endereço pelo CEP">
          <StrokeIcon d={Icons.search} size={16} />
        </button>
        <TextField label="País" value={form.country} onChange={(v) => set('country', v)} disabled={locked} />
        {loading ? (
          <FieldSkeleton label="UF" />
        ) : (
          <SelectField label="UF" required value={form.state} onChange={(v) => set('state', v)} error={errors.state} placeholder="UF" options={ESTADOS.map((u) => ({ value: u.uf, label: `${u.uf} — ${u.n}` }))} disabled={locked} />
        )}
      </div>
      {zipMessage && <div style={{ fontSize: 12, color: 'var(--forest-700)' }}>{zipMessage}</div>}
      {status === 'idle' && (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Informe o CEP para preencher o endereço. Os campos abrem após a busca.</div>
      )}
      {loading ? (
        <>
          <div className="pa-grid-2">
            <FieldSkeleton label="Cidade" />
            <FieldSkeleton label="Bairro" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr', gap: 16 }}>
            <FieldSkeleton label="Endereço" />
            <FieldSkeleton label="Número" />
            <FieldSkeleton label="Complemento" />
          </div>
        </>
      ) : (
        <>
          <div className="pa-grid-2">
            <TextField label="Cidade" required value={form.city} onChange={(v) => set('city', v)} error={errors.city} disabled={locked} />
            <TextField label="Bairro" required value={form.district} onChange={(v) => set('district', v)} error={errors.district} disabled={locked} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr', gap: 16 }}>
            <TextField label="Endereço" required value={form.street} onChange={(v) => set('street', v)} error={errors.street} placeholder="Rua, avenida…" disabled={locked} />
            <TextField label="Número" required value={form.number} onChange={(v) => set('number', v)} error={errors.number} placeholder="Nº ou S/N" disabled={locked} />
            <TextField label="Complemento" value={form.complement} onChange={(v) => set('complement', v)} placeholder="Sala, bloco… (opcional)" disabled={locked} />
          </div>
        </>
      )}
    </div>
  );
}

export function StepContactAndRepresentative({ form, set, errors }: StepProps) {
  // Editing any field of a person block drops the picked Party id, so the payload always describes what
  // is on screen (10 B8.4). `editPerson` is where that rule lives; these two only name the block.
  const setContact: SetField = (key, value) => editPerson(set, key, value, 'focalContactPartyId');
  const setRepresentative: SetField = (key, value) => editPerson(set, key, value, 'representativePartyId');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div className="pa-eyebrow" style={{ marginBottom: 10 }}>Contato focal</div>
        <div className="pa-grid-2">
          <PartyNameField
            label="Nome"
            required
            value={form.focalContactName}
            onChange={(v) => setContact('focalContactName', v)}
            error={errors.focalContactName}
            partyId={form.focalContactPartyId}
            onPick={(p) => fillFocalContact(set, p)}
            buildDraft={() => partyDraft(form.focalContactName, '', form.focalContactEmail, form.focalContactPhone)}
          />
          <PhoneField value={form.focalContactPhone} onChange={(v) => setContact('focalContactPhone', v)} error={errors.focalContactPhone} />
        </div>
        <div style={{ marginTop: 16 }}>
          <TextField label="E-mail" value={form.focalContactEmail} onChange={(v) => setContact('focalContactEmail', v)} error={errors.focalContactEmail} placeholder="contato@empresa.com.br (opcional)" />
        </div>
      </div>
      <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 18 }}>
        <div className="pa-eyebrow" style={{ marginBottom: 10 }}>Representante legal</div>
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginBottom: 12 }}>
          Quem assina o contrato da empresa. É para o e-mail dele que o documento é enviado.
        </div>
        <div className="pa-grid-2">
          <PartyNameField
            label="Nome"
            required
            value={form.representativeName}
            onChange={(v) => setRepresentative('representativeName', v)}
            error={errors.representativeName}
            partyId={form.representativePartyId}
            onPick={(p) => fillRepresentative(set, p)}
            buildDraft={() => partyDraft(form.representativeName, form.representativeCpf, form.representativeEmail, form.representativePhone)}
          />
          <TextField label="CPF" value={maskCPF(form.representativeCpf)} onChange={(v) => setRepresentative('representativeCpf', maskCPF(v))} error={errors.representativeCpf} placeholder="000.000.000-00 (opcional)" mono />
        </div>
        <div className="pa-grid-2" style={{ marginTop: 16 }}>
          <TextField label="E-mail" required value={form.representativeEmail} onChange={(v) => setRepresentative('representativeEmail', v)} error={errors.representativeEmail} />
          <PhoneField value={form.representativePhone} onChange={(v) => setRepresentative('representativePhone', v)} error={errors.representativePhone} />
        </div>
      </div>
      <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 18 }}>
        <ContactEmailField form={form} set={set} errors={errors} />
      </div>
    </div>
  );
}

/** One option of {@link ContactEmailField}: its label and the line under it. */
interface ContactEmailOption {
  value: ContactEmailSource;
  label: string;
  hint: string;
  disabled: boolean;
}

/**
 * Which e-mail is the Empresa's contact e-mail — a radio group of three, the representative's by default.
 *
 * Each option shows the address it would use, so the choice is made against what is on screen. The focal
 * contact's is disabled while that field is empty — but only for picking it: once chosen, clearing the
 * field leaves it chosen and the validation says so, rather than the screen quietly changing the answer.
 */
function ContactEmailField({ form, set, errors }: Pick<StepProps, 'form' | 'set' | 'errors'>) {
  const id = useId();
  const representativeEmail = form.representativeEmail.trim();
  const focalContactEmail = form.focalContactEmail.trim();

  const options: ContactEmailOption[] = [
    {
      value: 'Representative',
      label: 'Usar o e-mail do representante',
      hint: representativeEmail || 'Preencha o e-mail do representante acima.',
      disabled: false,
    },
    {
      value: 'FocalContact',
      label: 'Usar o e-mail do contato focal',
      hint: focalContactEmail || 'Indisponível: o contato focal está sem e-mail.',
      disabled: !focalContactEmail && form.contactEmailSource !== 'FocalContact',
    },
    { value: 'Other', label: 'Outro e-mail', hint: 'Um endereço só para o contato da empresa.', disabled: false },
  ];

  return (
    <div>
      <div className="pa-eyebrow" style={{ marginBottom: 10 }}>E-mail de contato da Empresa</div>
      <div id={`${id}-help`} style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginBottom: 12 }}>
        É o e-mail que o Checkout mostra como contato da empresa.
      </div>
      <div
        role="radiogroup"
        aria-label="E-mail de contato da Empresa"
        aria-describedby={`${id}-help`}
        style={{ display: 'grid', gap: 'var(--space-2)' }}
      >
        {options.map((option) => {
          const active = option.value === form.contactEmailSource;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-describedby={`${id}-${option.value}`}
              disabled={option.disabled}
              onClick={() => set('contactEmailSource', option.value)}
              style={{
                display: 'grid',
                gap: 'var(--space-1)',
                padding: 'var(--space-3) var(--space-4)',
                textAlign: 'left',
                cursor: option.disabled ? 'not-allowed' : 'pointer',
                border: `1px solid ${active ? 'var(--forest-900)' : 'var(--border-default)'}`,
                boxShadow: active ? 'inset 0 0 0 1px var(--forest-900)' : 'none',
                background: active ? 'var(--sage-100)' : 'var(--cream-50)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-sans)',
                opacity: option.disabled ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink-900)' }}>{option.label}</span>
              <span id={`${id}-${option.value}`} style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                {option.hint}
              </span>
            </button>
          );
        })}
      </div>
      {errors.contactEmailSource && (
        <div role="alert" style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)', marginTop: 'var(--space-2)' }}>
          {errors.contactEmailSource}
        </div>
      )}
      {form.contactEmailSource === 'Other' && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <TextField
            label="E-mail de contato"
            required
            type="email"
            inputMode="email"
            value={form.contactEmail}
            onChange={(v) => set('contactEmail', v)}
            error={errors.contactEmail}
            placeholder="contato@empresa.com.br"
          />
        </div>
      )}
    </div>
  );
}

/** Permissions for the type-2 producer login created automatically for the legal representative. */
export function StepPortalPermissions({ form, set }: StepProps) {
  const selected = new Set(form.portalPermissions);
  const toggle = (permission: string) => {
    const next = new Set(form.portalPermissions);
    if (next.has(permission)) next.delete(permission);
    else next.add(permission);
    set('portalPermissions', LEGACY_PORTAL_PERMISSIONS.filter((item) => next.has(item)));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Notice tone="neutral">
        Estas permissões serão aplicadas ao usuário tipo 2 criado automaticamente para o representante no
        Portal antigo. O telefone informado no passo anterior também será enviado. Ambos são opcionais.
      </Notice>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ color: 'var(--fg-muted)', fontSize: 12.5 }}>
          {form.portalPermissions.length} de {LEGACY_PORTAL_PERMISSIONS.length} permissões selecionadas
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => set('portalPermissions', [...DEFAULT_LEGACY_PORTAL_PERMISSIONS])}
            style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)', color: 'var(--forest-800)', padding: '7px 10px', cursor: 'pointer', fontSize: 12 }}
          >
            Restaurar padrão
          </button>
          <button
            type="button"
            onClick={() => set('portalPermissions', [])}
            style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)', color: 'var(--fg-muted)', padding: '7px 10px', cursor: 'pointer', fontSize: 12 }}
          >
            Remover todas
          </button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
        {LEGACY_PORTAL_PERMISSIONS.map((permission) => (
          <label
            key={permission}
            style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, padding: '9px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: selected.has(permission) ? 'var(--sage-100)' : 'var(--cream-50)', cursor: 'pointer', fontSize: 13 }}
          >
            <input
              type="checkbox"
              checked={selected.has(permission)}
              onChange={() => toggle(permission)}
              aria-label={permission}
            />
            <span>{permission}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Where the Empresa's money lands — the same account the Conta bancária screen manages (13).
 *
 * The bank is **one** field picked from the legacy's own dictionary, not a name typed next to a code: the
 * two come from the same row, so they cannot disagree, and the operator finds their bank by typing its
 * name. It was a hard-coded list of eleven, which meant that an Empresa banking anywhere else could not be
 * registered at all.
 */
export function StepReceivingAccount({ form, set, errors }: StepProps) {
  const banks = useBanks();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ background: 'var(--cream-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 12.5, color: 'var(--fg-muted)' }}>
        Opcional — a conta pode ser cadastrada depois, na aba <strong>Conta bancária</strong> da empresa. Se
        preencher, informe o bloco inteiro: uma conta pela metade não recebe transferência.
      </div>
      <TextField
        label="Titular da conta (razão social bancária)"
        hint="A razão social exatamente como o banco a registra. Divergência aqui é o motivo mais comum de uma transferência voltar."
        value={form.bankHolderLegalName}
        onChange={(v) => set('bankHolderLegalName', v)}
        error={errors.bankHolderLegalName}
      />
      <div className="pa-grid-2">
        <ComboField
          label="Banco"
          hint="Vem do cadastro de bancos do sistema legado. Digite o nome ou o código — o código é o que identifica o banco na transferência, por isso ele e o nome são escolhidos juntos."
          value={form.bankCode}
          onChange={(code) => {
            const bank = banks.data?.find((b) => b.code === code);
            set('bankCode', code);
            // The name travels with the code, and is only ever what the catalogue says.
            set('bankName', bank?.name ?? '');
          }}
          options={(banks.data ?? []).map((bank) => ({
            value: bank.code,
            label: `${bank.code} · ${bank.name}`,
            // Searched too, so the registered name finds a bank the short one does not.
            secondary: bank.fullName ?? undefined,
          }))}
          placeholder={banks.isPending ? 'Carregando bancos…' : 'Busque pelo nome ou código'}
          notFoundMessage="Nenhum banco com esse nome ou código."
          error={errors.bankCode ?? errors.bankName}
          disabled={banks.isPending}
        />
        <div>
          <label className="pa-form-label">Tipo de conta</label>
          {/* O rótulo é PT-BR; o valor viaja em inglês, como o contrato da API espera. */}
          <Segmented
            options={[{ value: 'Checking' as const, label: 'Corrente' }, { value: 'Savings' as const, label: 'Poupança' }]}
            value={form.bankAccountType}
            onChange={(v) => set('bankAccountType', v)}
            size="sm"
          />
          {errors.bankAccountType && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{errors.bankAccountType}</div>}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: 16 }}>
        <TextField label="Agência" hint="De 3 a 5 dígitos, sem o dígito verificador da agência." placeholder="0001" value={form.bankBranch} onChange={(v) => set('bankBranch', maskDigits(v, 5))} error={errors.bankBranch} mono />
        <TextField label="Número da conta" hint="De 4 a 12 dígitos, sem o dígito verificador — ele vai no campo ao lado." placeholder="908716325" value={form.bankAccountNumber} onChange={(v) => set('bankAccountNumber', maskDigits(v, 12))} error={errors.bankAccountNumber} mono />
        <TextField label="Dígito" hint="O dígito verificador da conta: 1 ou 2 caracteres. Alguns bancos usam X." placeholder="2" value={form.bankAccountDigit} onChange={(v) => set('bankAccountDigit', v.replace(/[^0-9Xx]/g, '').slice(0, 2))} error={errors.bankAccountDigit} mono />
      </div>

      {banks.isError && (
        <Notice tone="warning">
          Não foi possível carregar a lista de bancos agora. Tente novamente em instantes — ou deixe a conta
          para depois, na aba Conta bancária.
        </Notice>
      )}
    </div>
  );
}

/**
 * What the TMR charges this Empresa — a section of its own, never part of the account block.
 *
 * **Nothing here is saved**, and the notice says so where the fields are rather than in a footnote: the
 * Empresa's real rates are a model of their own — per product, per installment band, with temporary
 * windows (08) — and they are managed on the Taxas tab once the Empresa exists. The fields stay because
 * they are what the operator has in hand while registering; treating them as a record would be the lie.
 */
export function StepRates({ form, set, errors }: StepProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ background: 'var(--cream-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 12.5, color: 'var(--fg-muted)' }}>
        Estes valores <strong>não são salvos</strong> neste cadastro — a empresa nasce com as taxas padrão da
        TMR. As taxas de verdade (por produto e faixa de parcelas) são cadastradas na aba{' '}
        <strong>Taxas</strong> da empresa.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        <TextField label="Taxa de Parcelamento ao mês" inputMode="decimal" value={form.monthlyInterest} onChange={(v) => set('monthlyInterest', v)} error={errors.monthlyInterest} suffix="%" />
        <TextField label="Descomplicado" inputMode="decimal" value={form.rateStandard} onChange={(v) => set('rateStandard', v)} error={errors.rateStandard} suffix="%" />
        <TextField label="Compartilhado" inputMode="decimal" value={form.rateShared} onChange={(v) => set('rateShared', v)} error={errors.rateShared} suffix="%" />
        <TextField label="Coprodutor" inputMode="decimal" value={form.rateCoProducer} onChange={(v) => set('rateCoProducer', v)} error={errors.rateCoProducer} suffix="%" />
      </div>
      <TextField label="Limite de parcelamento" value={form.creditLimit} disabled suffix="%" />
    </div>
  );
}

export function ReviewSection({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) {
  return (
    <div className="pa-card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div className="pa-eyebrow">{title}</div>
        <button type="button" onClick={onEdit} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--forest-700)', fontSize: 12.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 }}>
          <StrokeIcon d={Icons.pen} size={13} /> Editar
        </button>
      </div>
      {children}
    </div>
  );
}

/**
 * The chosen classification on the review step — the label, never the id.
 *
 * The form holds the domain item id, which is the right thing to submit and the wrong thing to show: "160"
 * tells the operator nothing about what they are about to confirm.
 */
function ReviewClassification({ value }: { value: string }) {
  const query = useClassifications();
  const chosen = query.data?.find((c) => String(c.id) === value);

  if (!value) return <>—</>;
  return <>{chosen ? classificationLabel(chosen) : '—'}</>;
}

export function StepReview({ form, goTo }: { form: CompanyForm; goTo: (index: number) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ReviewSection title="Empresa" onEdit={() => goTo(0)}>
        <Field label="Nome fantasia">{form.name}</Field>
        <Field label="Razão social">{form.legalName}</Field>
        <Field label="CNPJ">{maskCNPJ(form.cnpj)}</Field>
        <Field label="Domínio">{form.subdomain}</Field>
        <Field label="Origem">{form.acquisitionOrigin || '—'}</Field>
        <Field label="Classificação"><ReviewClassification value={form.classification} /></Field>
        <Field label="Segmento">{form.segment ? segmentLabel(form.segment) : '—'}</Field>
        <Field label="Cobrança especial">{form.specialBilling ? 'Sim' : 'Não'}</Field>
      </ReviewSection>

      <ReviewSection title="Endereço" onEdit={() => goTo(1)}>
        <Field label="Endereço">
          {form.street}, {form.number}
          {form.complement ? ` – ${form.complement}` : ''}
        </Field>
        <Field label="Bairro / Cidade">{form.district} · {form.city}/{form.state}</Field>
        <Field label="CEP">{maskCEP(form.zipCode)}</Field>
      </ReviewSection>

      <ReviewSection title="Contato & representante" onEdit={() => goTo(2)}>
        <Field label="Contato focal">
          {[form.focalContactName, displayPhone(form.focalContactPhone) || form.focalContactEmail].filter(Boolean).join(' · ') || '—'}
        </Field>
        <Field label="Representante legal">
          {[form.representativeName, form.representativeEmail].filter(Boolean).join(' · ')}
        </Field>
        <Field label="E-mail de contato">{contactEmailSummary(formContactEmail(form))}</Field>
      </ReviewSection>

      <ReviewSection title="Acesso no Portal antigo" onEdit={() => goTo(3)}>
        <Field label="Permissões">
          {form.portalPermissions.length > 0
            ? `${form.portalPermissions.length} selecionadas · ${form.portalPermissions.join(', ')}`
            : 'Nenhuma permissão selecionada'}
        </Field>
      </ReviewSection>

      <ReviewSection title="Conta de recebimento" onEdit={() => goTo(4)}>
        {form.bankName ? (
          <>
            <Field label="Titular">{form.bankHolderLegalName}</Field>
            <Field label="Banco">{form.bankCode} · {form.bankName}</Field>
            <Field label="Tipo de conta">{form.bankAccountType === 'Checking' ? 'Corrente' : 'Poupança'}</Field>
            <Field label="Agência / Conta">{form.bankBranch} / {form.bankAccountNumber}-{form.bankAccountDigit}</Field>
          </>
        ) : (
          <Field label="Conta">Não informada — pode ser cadastrada depois, na aba Conta bancária</Field>
        )}
      </ReviewSection>

      <ReviewSection title="Taxas" onEdit={() => goTo(5)}>
        <Field label="Taxas">Não enviadas neste cadastro — a empresa recebe as taxas padrão da TMR.</Field>
      </ReviewSection>
    </div>
  );
}
